import React, { useEffect, useRef, useState } from "react";
import Navbar from "../layouts/Navbar";
import Footer from "../layouts/Footer";
import "./LoanApplication.css";

const INCOME = [
  "I am employed and I have monthly payslips",
  "I am self employed",
  "I receive income from my investments",
  "I receive rental income",
  "Other (Please specify below)",
];
const SOURCES = [
  "Ask Property Website", "Facebook", "Twitter", "Tik Tok", "Instagram",
  "LinkedIn", "Volk Cloud", "Other (Please specify the person who referred you, if applicable)",
];
const EMPTY = { email: "", name: "", phone: "", has_otp: "", applied_elsewhere: "",
  bank: "", finance_amount: "", income: "", income_other: "", consent: "",
  sources: [], source_other: "", copy_requested: false, website: "" };
const API_BASE = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");

async function newToken(formType) {
  if (!API_BASE || !/^https:\/\//.test(API_BASE)) throw new Error("The application service is unavailable. Please email levis.mbanya@askproperty.co.za.");
  const response = await fetch(API_BASE + "/loan-applications/token?form_type=" + encodeURIComponent(formType), {
    headers: { Accept: "application/json" }, cache: "no-store", credentials: "omit",
  });
  const result = await response.json();
  if (!response.ok || !result.token) throw new Error("We could not load the form. Please use Retry below.");
  return result.token;
}

function FieldError({ name, errors }) {
  return errors[name] ? <span className="ap-loan-error" id={name + "-error"}>{errors[name][0]}</span> : null;
}

function TextField({ name, label, required, type = "text", maxLength = 150, value, onChange, errors, autoComplete }) {
  return <div className="ap-loan-field">
    <label htmlFor={name}>{label}{required && <span aria-hidden="true"> *</span>}</label>
    <input id={name} name={name} type={type} value={value} required={required}
      maxLength={maxLength} onChange={onChange} autoComplete={autoComplete}
      aria-invalid={!!errors[name]} aria-describedby={errors[name] ? name + "-error" : undefined} />
    <FieldError name={name} errors={errors} />
  </div>;
}

function Choices({ name, label, required, options, value, onChange, errors, multiple = false }) {
  return <fieldset className="ap-loan-choices" aria-describedby={errors[name] ? name + "-error" : undefined}>
    <legend>{label}{required && <span aria-hidden="true"> *</span>}</legend>
    {options.map((option, index) => <label className="ap-loan-option" key={option}>
      <input type={multiple ? "checkbox" : "radio"} name={name} id={name + "-" + index}
        value={option} required={required && !multiple}
        checked={multiple ? value.includes(option) : value === option}
        onChange={onChange} aria-invalid={!!errors[name]} />
      <span>{option}</span>
    </label>)}
    <FieldError name={name} errors={errors} />
  </fieldset>;
}

export default function LoanApplication({ formType }) {
  const isLoan = formType === "home-loan";
  const title = isLoan ? "Home Loan Application - South Africa" : "Get pre-approved for a home loan - South Africa";
  const [values, setValues] = useState({ ...EMPTY });
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState("");
  const [success, setSuccess] = useState(null);
  const sending = useRef(false);
  const statusRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const oldTitle = document.title;
    document.title = title + " | Ask Property";
    window.scrollTo(0, 0);
    newToken(formType).then(value => { if (alive) setToken(value); })
      .catch(error => { if (alive) setNotice(error.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; document.title = oldTitle; };
  }, [formType, title]);

  useEffect(() => {
    if ((notice || success) && statusRef.current) statusRef.current.focus();
  }, [notice, success]);

  const change = event => {
    const { name, value, checked, type } = event.target;
    setValues(previous => ({ ...previous, [name]: name === "sources"
      ? (checked ? [...previous.sources, value] : previous.sources.filter(item => item !== value))
      : type === "checkbox" ? checked : value }));
    setErrors(previous => ({ ...previous, [name]: undefined }));
  };

  const retry = async () => {
    setLoading(true); setNotice("");
    try { setToken(await newToken(formType)); }
    catch (error) { setNotice(error.message); }
    finally { setLoading(false); }
  };

  const submit = async event => {
    event.preventDefault();
    if (sending.current || !token) return;
    sending.current = true; setBusy(true); setNotice(""); setErrors({});
    try {
      const response = await fetch(API_BASE + "/loan-applications", {
        method: "POST", credentials: "omit",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, form_type: formType, token }),
      });
      let result;
      try { result = await response.json(); } catch (_) { result = {}; }
      if (response.ok && result.success) {
        setSuccess({ ...result, consent: values.consent, copy_requested: values.copy_requested });
        setValues({ ...EMPTY });
        window.scrollTo(0, 0);
      } else if (result.refresh_token) {
        setToken("");
        setToken(await newToken(formType));
        setNotice("Your form session has been renewed. Your answers are still here. Please submit again.");
      } else {
        setErrors(result.errors || {});
        setNotice(response.status === 429
          ? "Too many attempts. Please try again in an hour, or email levis.mbanya@askproperty.co.za."
          : result.message || "We could not confirm submission. Your answers are still here. Please try again.");
      }
    } catch (_) {
      setNotice("We could not confirm submission. Check your connection and try again. Your answers are still here.");
    } finally { sending.current = false; setBusy(false); }
  };

  const text = (name, label, required = false, type = "text", maxLength = 150, autoComplete) =>
    <TextField name={name} label={label} required={required} type={type} maxLength={maxLength}
      autoComplete={autoComplete} value={values[name]} onChange={change} errors={errors} />;
  const choices = (name, label, options, required = true, multiple = false) =>
    <Choices name={name} label={label} options={options} required={required} multiple={multiple}
      value={values[name]} onChange={change} errors={errors} />;

  return <>
    <Navbar />
    <main className="ap-loan-page">
      <div className="ap-loan-wrap">
        <a className="ap-loan-back" href="/">← Back to Ask Property</a>
        <header className="ap-loan-hero">
          <p className="ap-loan-eyebrow">ASK PROPERTY · HOME FINANCE</p>
          <h1>{title}</h1>
          {isLoan ? <>
            <p>Recognizing that your home purchase is a significant life investment, we are here to transform the process into an exciting and rewarding experience. We work with various banks to get you the best deal.</p>
            <p><strong>The banks pay us a once-off fee, so our service to you is completely free.</strong></p>
          </> : <p>Getting pre-approved before purchasing a home is essential. It clarifies what you as the buyer can afford and helps focus the search on suitable properties. Pre-approval shows the sellers that you are serious and financially ready, offering you a competitive edge.</p>}
          <p>Once you submit your details, you will be contacted by one of our dedicated bank consultants to request your supporting documents and work on your application immediately, if you consent to being contacted.</p>
        </header>
        {success ? <section className="ap-loan-card ap-loan-success" tabIndex="-1" ref={statusRef} role="status">
          <span className="ap-loan-tick" aria-hidden="true">✓</span>
          <h2>Thank you. Your application has been submitted.</h2>
          <p>Your reference is <strong>{success.reference}</strong>.</p>
          <p>{success.consent === "Yes" ? "Our team will follow up using the contact details you provided." : "You selected No to contact consent. This preference has been included with your application."}</p>
          {success.copy_requested && <p>{success.copy_sent ? "A copy of your responses has been sent to your email address. Please check your spam folder too." : "Your application was submitted, but we could not send your copy. Contact levis.mbanya@askproperty.co.za with your reference if you need it."}</p>}
          <a className="ap-loan-button" href="/">Return to home page</a>
        </section> : <form className="ap-loan-card" onSubmit={submit}>
          <div className="ap-loan-form-heading"><h2>Your application</h2><p>Fields marked * are required.</p></div>
          <p className="ap-loan-privacy">Your personal information and privacy will be handled in accordance with the POPI Act and we do not sell your information to any third party. <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Read our privacy policy</a>.</p>
          {notice && <div className="ap-loan-notice" role="alert" tabIndex="-1" ref={statusRef}>{notice}</div>}
          <fieldset className="ap-loan-fields" disabled={busy}>
            {text("email", "Email", true, "email", 254, "email")}
            {isLoan && <>
              {choices("has_otp", "Do you have a signed offer to purchase (OTP) document?", ["Yes", "No"])}
              {choices("applied_elsewhere", "Did you apply for finance from any other bank?", ["Yes", "No"])}
              {text("bank", "If yes, specify the bank", false, "text", 200)}
              {text("finance_amount", "How much finance are you applying for?", true, "text", 80)}
            </>}
            {text("name", "Your name and surname", true, "text", 150, "name")}
            {text("phone", "Your contact number", true, "tel", 50, "tel")}
            {choices("income", "Source of Income", INCOME)}
            {text("income_other", "If you selected Other, please specify here", false, "text", 1000)}
            {choices("consent", "Do you give us consent to contact you?", ["Yes", "No"])}
            {choices("sources", "Where did you hear about us?", SOURCES, false, true)}
            {text("source_other", "If you selected Other, please specify", false, "text", 1000)}
            <div className="ap-loan-trap" aria-hidden="true"><label>Leave this blank<input name="website" value={values.website} onChange={change} tabIndex="-1" autoComplete="off" /></label></div>
            <label className="ap-loan-option ap-loan-copy"><input type="checkbox" name="copy_requested" checked={values.copy_requested} onChange={change} /><span>Send me a copy of my responses.</span></label>
            <button className="ap-loan-button" type="submit" disabled={busy || loading || !token}>
              {busy ? "Submitting…" : loading ? "Preparing form…" : "Submit application"}
            </button>
          </fieldset>
          {!token && !loading && <button type="button" className="ap-loan-retry" onClick={retry}>Retry loading form</button>}
          <p className="ap-loan-fine">Your application is sent to the Ask Property team. Submitting this form does not guarantee credit approval.</p>
        </form>}
      </div>
    </main>
    <Footer />
  </>;
}
